import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme, AlertBanner } from '@flowcore-water/ui';
import { useAuth } from '../contexts/AuthContext';
import { libraryService } from '../services/libraryService';
import { MANUAL_SECTIONS } from '../data/trainingManual';
import type { LibraryItem } from '../types';

const TYPE_LABEL: Record<LibraryItem['type'], string> = {
  pdf: 'Document',
  weblink: 'Link',
  article: 'Article',
};

/**
 * Water Well Technician Training — the training manual.
 * Each section (category) lists its weeks; each week is a Library item
 * tagged with `manualSection` + `manualWeek`. Visible to all signed-in users;
 * only instructors/admins can add or edit weeks.
 */
export const WaterWellTechTraining: React.FC = () => {
  const { t } = useTheme();
  const { user } = useAuth();
  const canEdit = user?.role === 'instructor' || user?.role === 'admin';

  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    libraryService
      .getAll()
      .then((all) => setItems(all.filter((i) => i.manualSection)))
      .catch((err) => {
        console.error('Error loading training manual:', err);
        setError('Failed to load the training manual');
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: t.textPrimary }}>
          Water Well Technician Training
        </h1>
        <p className="mt-2" style={{ color: t.textSecondary }}>
          Training resources for water well technicians.
        </p>
      </div>

      {error && <AlertBanner variant="error" message={error} />}

      {MANUAL_SECTIONS.map((section) => {
        const weeks = items
          .filter((i) => i.manualSection === section.id)
          .sort((a, b) => (a.manualWeek ?? 0) - (b.manualWeek ?? 0));
        const nextWeek = weeks.reduce((max, w) => Math.max(max, w.manualWeek ?? 0), 0) + 1;

        return (
          <section key={section.id} className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold" style={{ color: t.textPrimary }}>
                  {section.title}
                </h2>
                <p className="mt-1 text-sm" style={{ color: t.textSecondary }}>
                  {section.description}
                </p>
              </div>
              {canEdit && (
                <Link
                  to={`/library/new?manual=${section.id}&week=${nextWeek}`}
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                  style={{ background: t.accentDim, color: t.textPrimary }}
                >
                  + Add Week {nextWeek}
                </Link>
              )}
            </div>

            {loading ? (
              <p style={{ color: t.textMuted }}>Loading...</p>
            ) : weeks.length === 0 ? (
              <div
                className="rounded-lg p-8 text-center"
                style={{ background: t.cardBg, border: `1px solid ${t.border}` }}
              >
                <p style={{ color: t.textMuted }}>
                  No weeks yet.{canEdit ? ' Use “Add Week 1” to post the first one.' : ''}
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {weeks.map((week) => (
                  <li
                    key={week.id}
                    className="flex items-center gap-4 rounded-lg p-4"
                    style={{ background: t.cardBg, border: `1px solid ${t.border}` }}
                  >
                    <div
                      className="shrink-0 rounded-md px-3 py-2 text-center text-xs font-bold uppercase tracking-wide"
                      style={{ background: t.accentDim, color: t.textPrimary, minWidth: 72 }}
                    >
                      Week {week.manualWeek ?? '?'}
                    </div>
                    <Link to={`/library/${week.slug}`} className="min-w-0 flex-1">
                      <p className="font-semibold" style={{ color: t.textPrimary }}>
                        {week.title}
                      </p>
                      {week.description && (
                        <p className="mt-1 truncate text-sm" style={{ color: t.textSecondary }}>
                          {week.description}
                        </p>
                      )}
                    </Link>
                    <span className="hidden text-xs sm:inline" style={{ color: t.textMuted }}>
                      {TYPE_LABEL[week.type]}
                    </span>
                    {canEdit && (
                      <Link
                        to={`/library/edit/${week.id}`}
                        className="text-sm"
                        style={{ color: t.accent }}
                      >
                        Edit
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
};
